import {
  ModalProps,
  useModal,
  createUseModal,
} from "@imminently/react/context/modals";
import { Text } from "@imminently/react/ui/core/text";
import { useNotify } from "@imminently/react/store/notifications";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useState } from "react";

import { DataTransferIcon } from "@/assets/icons";
import { Icon } from "@/components/Icon";
import PatientCardModal from "@/components/PatientCardModal";
import { PatientEncounter, Practitioners, UserAccount } from "@/resources/types";
import { useUsers } from "@/resources/users";
import { encounterService } from "@/services";
import { css } from "@/theme";

const styles = {
  practitionerName: {
    flex: 1,
    bgcolor: "grey.50",
    color: "grey",
    borderRadius: 1,
    display: "flex",
    alignItems: "center",
  },
  newPractitionerWrapper: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  selectField: css`
    & legend {
      width: 0;
    }

    & .MuiSelect-select {
      padding-top: 10px;
      padding-bottom: 10px;
    }
  `,
  notes: css`
    .MuiInputBase-multiline {
      padding-top: 14px;
      padding-bottom: 14px;
    }

    textarea {
      height: 100% !important;
    }
  `,
};

const PRACTITIONER_TITLES = {
  [Practitioners.SNR_DOCTOR]: "Senior Doctor",
  [Practitioners.DOCTOR]: "Doctor",
  [Practitioners.NURSE]: "Nurse",
};

type PractitionerFieldProps = {
  value: string | "";
  currentPractitioner: string | undefined;
  allUsers: UserAccount[];
  type: Practitioners;
  onChange: (value: string) => void;
};

const PractitionerField = (props: PractitionerFieldProps) => {
  const { allUsers, type } = props;
  let searchType = type === 'snrDoctor' ? 'doctor' : type; // We don't have a senior doctor role at the moment
  let validUsers = allUsers.map((u: any) => {
    if (u.roles && u.roles.indexOf(searchType) > -1) {
      return u;
    }
  }).filter(x => x);
  return (
    <Stack gap={1}>
      <Text bold>{PRACTITIONER_TITLES[props.type]}</Text>

      <Stack direction="row" justifyContent="center">
        <Box sx={styles.practitionerName}>
          <Box px={2}>{props.currentPractitioner}</Box>
        </Box>

        <Box px={2} display="flex" alignItems="center">
          <Icon icon="arrowright" />
        </Box>

        <Box sx={styles.newPractitionerWrapper}>
          <FormControl sx={{ flex: 1 }}>
            <Select
              css={styles.selectField}
              value={props.value}
              onChange={(e) => props.onChange(e.target.value)}
            >
              <MenuItem value="">None</MenuItem>

              {validUsers && validUsers.map((user, index2) => (
                <MenuItem key={index2} value={user.identity_id}>
                  {user.firstName} {user.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Stack>
    </Stack>
  );
};

interface PatientHandoverModalProps {
  encounter: PatientEncounter;
}

const PatientHandoverModal = ({ props }: ModalProps<PatientHandoverModalProps>) => {
  const { popModal: close } = useModal();
  const notify = useNotify();
  const { data: users, query: usersQuery } = useUsers("handover-form", {});
  const { mutateAsync: updateEncounter, isLoading: isSubmitting } = encounterService.useUpdate();

  const { encounter } = props;

  const [practitioners, setPractitioners] = useState<{ type: string, id: string }[]>(
    encounter.practitioners
      ? encounter.practitioners.map((p) => ({ type: p.type, id: p.id }))
      : []
  );
  const [note, setNote] = useState("");

  const loading = usersQuery.isFetching || usersQuery.isLoading;

  const handlePractitionerChange = (type: string, value: string) => {
    // FIXME: There is a bug here where the backend does not accept removing
    // a practitioner from the encounter.

    setPractitioners((oldPractitioners) => {
      const curPrac = oldPractitioners.find((p) => p.type === type);
      if (typeof curPrac !== "undefined") {
        // Someone already has this role. We are either assigning it to
        // someone else, or setting it to "None" so that no one will have
        // this role

        if (value === "") {
          // They set to None - we need to do something special so the backend
          // knows we want to delete them and not assign the role. sending
          // nothing would make the backend thing we aren't touching that user
          return oldPractitioners.map(
            // @ts-ignore (it thinks that curPrac could be undefined, but that can't happen due to the above)
            (p) => p.type === type ? { type: "", id: curPrac.id } : p
          );
        } else {
          return oldPractitioners.map(
            (p) => p.type === type ? { type, id: value } : p
          );
        }
      } else {
        // No one has this role yet - let's add them in
        return [...oldPractitioners, { type, id: value }];
      }
    });
  };

  const handleSubmit = () => {
    updateEncounter({
      id: encounter.id,
      payload: {
        // @ts-ignore TS is being too strict, need a new method to define this type in createResourceService
        practitioners,
        note: note === "" ? undefined : { text: note },
      },
    })
      .then(() => {
        close();
      })
      .catch((e) => {
        notify.error(e.message ?? "Failed to update patient practitioners");
      });
  };

  return (
    <PatientCardModal
      title="Handover the following patient"
      encounter={encounter}
      onClose={() => close()}
      gap={3}
    >
      <Stack gap={2}>
        {loading ? (
          <CircularProgress sx={{ mx: "auto", my: 13 }} />
        ) : (
          Object.values(Practitioners).map((type, index) => (
            <PractitionerField
              key={index}
              value={practitioners.find((p) => p.type === type)?.id || ""}
              type={type}
              currentPractitioner={encounter.practitioners?.find((p) => p.type === type)?.shortName}
              allUsers={users}
              onChange={(val) => handlePractitionerChange(type, val)}
            />
          ))
        )}
      </Stack>

      <TextField
        css={styles.notes}
        multiline
        label="Handover notes..."
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />

      <Button
        sx={{ ml: "auto" }}
        color="success"
        disabled={usersQuery.isFetching || isSubmitting}
        startIcon={<DataTransferIcon />}
        onClick={handleSubmit}
      >
        Handover patient
      </Button>
    </PatientCardModal>
  );
};

export const usePatientHandoverModal = createUseModal(PatientHandoverModal);
